import { cn } from '@/lib';
import type { FunnelTemplateDefinition, FunnelTemplatePreviewRow } from '../../constants/funnelTemplates';

interface FunnelTemplateCardProps {
    template: FunnelTemplateDefinition;
    selected: boolean;
    onSelect: () => void;
}

export const FunnelTemplateCard = ({ template, selected, onSelect }: FunnelTemplateCardProps) => (
    <button
        type='button'
        onClick={onSelect}
        aria-pressed={selected}
        className={cn(
            'bg-canvas group flex h-full w-full cursor-pointer flex-col gap-5 overflow-hidden rounded-2xl border-2 p-5 text-left transition-all',
            selected
                ? 'border-brand-main ring-brand-subtle ring-4'
                : 'border-transparent shadow-sm hover:-translate-y-0.5 hover:shadow-md'
        )}
    >
        <TemplatePreview rows={template.preview} selected={selected} />
        <TemplateMeta label={template.label} description={template.description} />
    </button>
);

interface TemplatePreviewProps {
    rows: readonly FunnelTemplatePreviewRow[];
    selected: boolean;
}

const TemplatePreview = ({ rows, selected }: TemplatePreviewProps) => (
    <div
        className={cn(
            'flex flex-col gap-3 rounded-xl p-5 ring-1 transition-colors ring-inset',
            selected ? 'bg-brand-subtle/40 ring-brand-subtle' : 'bg-subtle ring-neutral-subtle'
        )}
    >
        {rows.map((row, idx) => (
            <PreviewRow
                key={`${row.title}-${idx}`}
                stepNumber={idx + 1}
                title={row.title}
                subtitle={row.subtitle}
                isLast={idx === rows.length - 1}
            />
        ))}
    </div>
);

interface PreviewRowProps {
    stepNumber: number;
    title: string;
    subtitle?: string;
    isLast: boolean;
}

const PreviewRow = ({ stepNumber, title, subtitle, isLast }: PreviewRowProps) => (
    <div className='relative flex items-center gap-3'>
        <div className='bg-canvas text-brand-main ring-brand-subtle text-caption-1-bold relative z-[1] flex size-10 shrink-0 items-center justify-center rounded-xl shadow-md ring-1'>
            {stepNumber}
        </div>
        <div className='flex min-w-0 flex-col gap-0.5'>
            <span className='text-label-sm-bold text-dark truncate'>{title}</span>
            {subtitle && <span className='text-caption-2-regular text-gray truncate'>{subtitle}</span>}
        </div>
        {!isLast && (
            <span
                aria-hidden
                className='from-brand-main/50 to-brand-main/10 absolute top-10 left-[19px] h-3.5 w-0.5 rounded-full bg-gradient-to-b'
            />
        )}
    </div>
);

interface TemplateMetaProps {
    label: string;
    description: string;
}

const TemplateMeta = ({ label, description }: TemplateMetaProps) => (
    <div className='mt-auto flex flex-col gap-1'>
        <span className='text-heading-h4 text-dark'>{label}</span>
        <span className='text-body-sm text-gray'>{description}</span>
    </div>
);
